import { SPLIT_TEST_STATUS } from "../strings";

const Z_ALPHA = 1.959963984540054; // two-sided 5 %
const Z_BETA = 0.8416212335729143; // power 80 %

/** Same formula as helper/split-test-stats.helper.js in the backend. */
export const sampleSizePerVariant = (baselineRate, mdeRelative) => {
  const p1 = Math.min(0.999, Math.max(0.001, baselineRate));
  const p2 = Math.min(0.999, p1 * (1 + mdeRelative));
  const delta = Math.abs(p2 - p1);
  if (!delta) return Infinity;
  const n =
    Math.pow(
      Z_ALPHA * Math.sqrt(2 * p1 * (1 - p1)) +
        Z_BETA * Math.sqrt(p1 * (1 - p1) + p2 * (1 - p2)),
      2
    ) /
    (delta * delta);
  return Math.ceil(n);
};

export const formatPercent = (value, digits = 1) =>
  value === null || value === undefined || Number.isNaN(value)
    ? "–"
    : `${(value * 100).toFixed(digits)} %`;

export const formatSignedPercent = (value, digits = 1) =>
  value === null || value === undefined || Number.isNaN(value)
    ? "–"
    : `${value > 0 ? "+" : ""}${(value * 100).toFixed(digits)} %`;

export const formatNumber = (value) =>
  value === null || value === undefined
    ? "–"
    : new Intl.NumberFormat("de-DE").format(value);

export const formatPValue = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "–";
  if (value < 0.0001) return "< 0,0001";
  return value.toFixed(4).replace(".", ",");
};

export const formatDaysRunning = (value) =>
  value === null || value === undefined || !Number.isFinite(value)
    ? "–"
    : Math.floor(value).toString();

export const formatDays = (value) => {
  if (value === null || value === undefined || !Number.isFinite(value))
    return "–";
  if (value < 1) return "< 1";
  return Math.ceil(value).toString();
};

export const STATUS_BADGE = {
  [SPLIT_TEST_STATUS.PROPOSED]: {
    color: "#6c757d",
    background: "#f1f3f5",
    border: "#ced4da"
  },
  [SPLIT_TEST_STATUS.RUNNING]: {
    color: "#0d6efd",
    background: "#e7f1ff",
    border: "#9ec5fe"
  },
  [SPLIT_TEST_STATUS.WON]: {
    color: "#198754",
    background: "#e8f6ee",
    border: "#a3cfbb"
  },
  [SPLIT_TEST_STATUS.LOST]: {
    color: "#dc3545",
    background: "#fdecee",
    border: "#f1aeb5"
  },
  [SPLIT_TEST_STATUS.NO_DIFFERENCE]: {
    color: "#6c757d",
    background: "#f1f3f5",
    border: "#ced4da"
  },
  [SPLIT_TEST_STATUS.CANCELLED]: {
    color: "#fd7e14",
    background: "#fff3e6",
    border: "#fecba1"
  },
  [SPLIT_TEST_STATUS.REVERTED]: {
    color: "#6f42c1",
    background: "#f1ecfa",
    border: "#c5b3e6"
  }
};

export const isEnded = (status) =>
  [
    SPLIT_TEST_STATUS.WON,
    SPLIT_TEST_STATUS.LOST,
    SPLIT_TEST_STATUS.NO_DIFFERENCE,
    SPLIT_TEST_STATUS.CANCELLED,
    SPLIT_TEST_STATUS.REVERTED
  ].includes(status);

export const ELEMENT_TYPES = {
  TEXT: "text",
  COLOR: "color",
  FEATURE: "feature"
};

export const elementTypeOf = (elements, key) =>
  elements?.find((e) => e.key === key)?.type || ELEMENT_TYPES.TEXT;

/** Readable form of a control / variant value for cards and the history. */
export const describeValue = (type, value, t) => {
  if (type === ELEMENT_TYPES.COLOR) return value || "";
  if (type === ELEMENT_TYPES.FEATURE) {
    if (!value) return "";
    const state = value.enabled
      ? t("splitTests.feature.on")
      : t("splitTests.feature.off");
    return value.template ? `${state} · ${value.template}` : state;
  }
  return value ?? "";
};
